import { gsap } from 'gsap'
import React, { useCallback, useRef } from 'react'

import CheckMark from 'components/icons/CheckMark'
import { Text, Title } from 'components/typo'
import delve from 'dlv'
import useAnimateInView from 'hooks/useAnimateInView'
import useRefs from 'hooks/useRefs'
import useTimeline from 'hooks/useTimeline'
import { createGroupRefs } from 'services/helpers'

import css from './styles.module.scss'

const List = ({ services = [], location, techStacks = [] }) => {
  const elementRef = useRef()
  const serviceTitleRef = useRef()
  const locationTitleRef = useRef()
  const techStacksTitleRef = useRef()
  const countries = delve(location, 'countries.data', [])

  const locationRefs = useRefs(countries.length)
  const servicesRefs = useRefs(services.length)
  const techStacksRefs = useRefs(techStacks.length)

  const tl = useTimeline({ paused: true }, (tl) => {
    gsap.set(elementRef.current, {
      opacity: 1,
    })

    const stagger = 0.1
    const group = createGroupRefs([
      [true, serviceTitleRef.current],
      [services, servicesRefs.current],
      [true, locationTitleRef.current],
      [true, locationRefs.current],
    ])

    if (group.length > 0) {
      tl.fromTo(
        group,
        { opacity: 0 },
        {
          opacity: 1,
          ease: 'none',
          duration: 0.3,
          stagger,
        }
      )

      tl.fromTo(
        group,
        {
          y: 30,
        },
        {
          y: 0,
          duration: 0.8,
          force3D: true,
          ease: 'elastic.out(0.25, 0.2)',
          stagger,
        },
        '<'
      )
    }
  })

  const [inViewRef] = useAnimateInView({
    triggerOnce: true,
    animateIn: () => tl.restart(true),
  })

  const setRef = useCallback((node) => {
    elementRef.current = node
    inViewRef(node)
  }, [])

  return (
    <div className={css.List} ref={setRef}>
      {services.length > 0 && (
        <>
          <Title
            forwardRef={serviceTitleRef}
            className={css.title}
            size="small"
          >
            Services
          </Title>
          <ul className={css.services}>
            {services.map(({ attributes }, index) => (
              <Text
                className={css.service}
                key={index}
                tag="li"
                forwardRef={servicesRefs.current[index]}
              >
                <CheckMark className={css.icon} theme={'purple'} />
                {attributes.name}
              </Text>
            ))}
          </ul>
        </>
      )}
      {countries.length > 0 && (
        <div className={css.location}>
          <Title
            className={css.title}
            size="small"
            forwardRef={locationTitleRef}
          >
            Location
          </Title>
          <ul className={css.services}>
            {countries
              .map(({ id, attributes }) => ({ id, ...attributes }))
              .map(({ name, country }, index) => {
                if (name || country) {
                  return (
                    <div
                      ref={locationRefs.current[index]}
                      key={index}
                      className={css.service}
                    >
                      <CheckMark className={css.icon} theme={'purple'} />
                      {name && <Text className={css.service}> {name}</Text>}
                      {country && (
                        <Text className={css.country}>
                          {name ? ', ' : ''}
                          {country.data.attributes.name}
                        </Text>
                      )}
                    </div>
                  )
                }
                return null
              })}
          </ul>
        </div>
      )}

      {techStacks.length > 0 && (
        <div className={css.techStacks}>
          <Title
            forwardRef={techStacksTitleRef}
            className={css.title}
            size="small"
          >
            Tech stacks
          </Title>
          <ul className={css.services}>
            {techStacks.map(({ attributes }, index) => (
              <Text
                className={css.service}
                key={index}
                tag="li"
                forwardRef={techStacksRefs.current[index]}
              >
                <CheckMark className={css.icon} theme={'purple'} />
                {attributes.name}
              </Text>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default List
